import { FC, memo } from 'react'

import { useForm, useStore } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { getRouteApi, useRouter } from '@tanstack/react-router'
import { adverseMediaRiskLevels } from 'commons/types/dictionaries'
import { Request } from 'commons/utils/request/request'
import { FF, LeaveConfirmation, Toast } from 'ui/components'
import { Dialog, DialogFace, DialogFooterActions } from 'ui/components/Dialog'
import { TextareaInput } from 'ui/components/InputV2'
import { Select } from 'ui/components/Select'
import { DialogCol, FormRow } from 'ui/themes/globalStyles'
import { AdverseMediaDetailsType } from '~bank-adverse-media/types/details'

import { handlerServerError } from '@orbit/errors'

import { RiskLevelDialogProps } from './RiskLevelDialogProps'

import * as SC from './styles'

export const SetRiskLevelDialog: FC<RiskLevelDialogProps> = memo(({ onClose }) => {
  const router = useRouter()

  const routeApi = getRouteApi('/_app/adverse-media/$id')
  const loaderData = routeApi.useLoaderData({ select: (s) => s }) as AdverseMediaDetailsType
  const { title } = loaderData

  const { mutateAsync: setRiskLevel } = useMutation<void, Error, { id: number; notes: string; riskLevel: string }>({
    mutationFn: (opts) => Request.put(Request.urls.bank.adverseMediaCaseChangeRiskLevel, opts),
    onSuccess: async () => {
      await router.invalidate()
      Toast.success('Risk Level updated successfully')
      onClose()
    },
    onError: (e) => handlerServerError(e)
  })

  const form = useForm({
    defaultValues: {
      notes: '',
      riskLevel: adverseMediaRiskLevels[adverseMediaRiskLevels.length - 1].value
    } as { notes: string; riskLevel: string },
    onSubmit: async ({ value }) => {
      await setRiskLevel({
        id: loaderData.id,
        notes: value.notes,
        riskLevel: value.riskLevel
      })
    }
  })

  return (
    <LeaveConfirmation preventLeaving={useStore(form.store, (s) => s.isDirty && !s.isSubmitting && !s.isSubmitted)}>
      <Dialog
        face={DialogFace.DEFAULT}
        id="risk-level-dialog"
        onClose={onClose}
        title={<SC.Title>Set Risk Level for {title}</SC.Title>}
        footerActions={[DialogFooterActions.SUBMIT, DialogFooterActions.CANCEL]}
        onSubmit={() => form.handleSubmit()}
      >
        <SC.Content>
          <FormRow>
            <DialogCol>
              <form.Field
                name="riskLevel"
                children={(field) => (
                  <FF
                    label="Select Risk Level"
                    field={field}
                    input={(inputProps) => (
                      <Select
                        data={adverseMediaRiskLevels.filter((item) => item.value !== 'unknown')}
                        data-qa="select-risk-level"
                        options-data-qa="select-risk-level"
                        {...inputProps}
                      />
                    )}
                  />
                )}
              />
            </DialogCol>
          </FormRow>
          <FormRow>
            <DialogCol>
              <form.Field
                name="notes"
                children={(field) => (
                  <FF
                    label="Notes"
                    field={field}
                    input={(inputProps) => <TextareaInput {...inputProps} data-qa="select-risk-level-notes" />}
                  />
                )}
              />
            </DialogCol>
          </FormRow>
        </SC.Content>
      </Dialog>
    </LeaveConfirmation>
  )
})
